import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingCTA from '../../components/feature/FloatingCTA';
import { galleryItems } from '../../mocks/galleryData';

type Category = 'all' | 'chimney' | 'airduct' | 'dryervent';

const filters: { key: Category; label: string }[] = [
  { key: 'all', label: 'All Work' },
  { key: 'chimney', label: 'Chimney Cleaning' },
  { key: 'airduct', label: 'Air Duct Cleaning' },
  { key: 'dryervent', label: 'Dryer Vent Cleaning' },
];

export default function GalleryPage() {
  const [filter, setFilter] = useState<Category>('all');
  const [sliders, setSliders] = useState<Record<string, number>>({});

  const filtered = filter === 'all' ? galleryItems : galleryItems.filter((g) => g.category === filter);
  const getPos = (id: string) => sliders[id] ?? 50;

  const handleDrag = (id: string, e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    setSliders((prev) => ({ ...prev, [id]: Math.min(95, Math.max(5, x)) }));
  };

  return (
    <>
      <Header />
      <FloatingCTA />

      <section className="pt-32 pb-16 bg-brand-dark">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 text-brand-amber text-xs font-bold uppercase tracking-widest mb-5">
            <div className="h-px w-8 bg-brand-amber" />
            Before &amp; After Gallery
            <div className="h-px w-8 bg-brand-amber" />
          </div>
          <h1 className="font-serif text-5xl md:text-6xl font-bold text-white mb-5">See Our Work</h1>
          <p className="text-white/70 text-xl">Drag the slider on each image to reveal the before and after transformation from real Jacksonville homes.</p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter */}
          <div className="flex flex-wrap gap-2 mb-10 justify-center">
            {filters.map((f) => (
              <button key={f.key} onClick={() => setFilter(f.key)} className={`px-5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${filter === f.key ? 'bg-brand-amber text-white' : 'bg-stone-100 text-brand-light hover:bg-amber-50 hover:text-brand-amber'}`}>
                {f.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((item) => (
              <div key={item.id} className="rounded-xl overflow-hidden border border-stone-200">
                <div className="relative h-72 cursor-col-resize select-none overflow-hidden" onMouseMove={(e) => handleDrag(item.id, e)}>
                  <img src={item.afterImage} alt={`After ${item.label}`} className="absolute inset-0 w-full h-full object-cover object-top" />
                  <div className="absolute inset-0 overflow-hidden" style={{ width: `${getPos(item.id)}%` }}>
                    <img src={item.beforeImage} alt={`Before ${item.label}`} className="w-full h-full object-cover object-top" style={{ width: `${10000 / getPos(item.id)}%`, maxWidth: 'none' }} />
                  </div>
                  <div className="absolute top-0 bottom-0 w-0.5 bg-white z-10" style={{ left: `${getPos(item.id)}%` }}>
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg">
                      <i className="ri-arrow-left-right-line text-brand-dark text-xs" />
                    </div>
                  </div>
                  <div className="absolute top-3 left-3 bg-black/60 text-white text-xs font-semibold px-2.5 py-1 rounded-md z-10">Before</div>
                  <div className="absolute top-3 right-3 bg-brand-amber text-white text-xs font-semibold px-2.5 py-1 rounded-md z-10">After</div>
                </div>
                <div className="p-4 bg-stone-50 border-t border-stone-100">
                  <div className="font-semibold text-brand-dark text-sm mb-1">{item.label}</div>
                  <div className="text-brand-warm-gray text-xs">{item.caption}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
